import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import {
  Account,
  AccountManagerReportFrequency,
  AccountVariableType,
  ItContactValue,
} from '@/client/accounts';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { TrainingVariablesSection } from '@/components/accounts/training-variables/TrainingVariablesSection';
import { WhitelistingModal } from '@/components/campaigns/simulations/modals';
import {
  useAccount,
  useAccountVariables,
  useSaveAccountVariable,
} from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { InlineMenu } from '@/ui/inline-menu';
import { FlexContainer } from '@/ui/styled-ui';
import { branchAdminCheck } from '@/utils/helpers';
import { isScPayingCustomer } from '@/utils/helpers/security-culture.helper';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';
import { WhitelistingStatusBanner } from './WhitelistingStatusBanner';

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  pathItems.push({
    label: currentAccount?.name,
    url: !currentAccount.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
    template: AppBreadCrumbTemplate,
  });

  if (
    currentAccount?.isSystem &&
    account &&
    can(Actions.READ, Subjects.ACCOUNTS)
  ) {
    pathItems.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  pathItems.push({
    label: t('account.trainingSettings'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_TRAINING_SETTINGS](
          account?.id as string,
        )
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_TRAINING_SETTINGS](),
    template: AppBreadCrumbTemplate,
  });

  return pathItems;
};

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom: 1px solid var(--gray-dark);
  padding-bottom: var(--xlarge-padding);

  &:not(:first-child) {
    padding-top: var(--xlarge-padding);
  }
`;

const StyledSectionHeading = styled.h2`
  font-size: 19px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  color: var(--black-main);
`;

const StyledSectionDescription = styled.p`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  margin-top: var(--xsmall-padding);
  color: var(--black-main);
`;

const StyledSubsectionHeading = styled.h3`
  font-size: var(--default-font-size);
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  margin-top: var(--default-padding);
  margin-bottom: var(--small-padding);
  color: var(--black-main);
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--small-font-size);
  font-weight: 700;
  cursor: pointer;
`;

const StyledCheckboxDescription = styled.p`
  font-size: var(--xsmall-font-size);
  line-height: 1.5;
  margin: var(--xxxsmall-padding) 0 0 0;
  color: var(--black-main);
`;

const StyledFollowUpToast = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 13px;
  margin-top: var(--medium-padding);
  padding: 18px 24px 20px 20px;
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;
  color: var(--black-main);

  .pi-exclamation-triangle {
    color: var(--yellow-dark);
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

export const AccountTrainingSettingsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { can } = usePermission();
  const { accountCanUseFeature } = useFeatureFlag();
  const accountId = currentAccount?.isSystem ? id : currentAccount?.id;
  const {
    account,
    isLoading: isAccountLoading,
    refetch: refetchAccount,
  } = useAccount({
    accountId,
  });

  const canUseTrainingVariables = accountCanUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
    account?.featureFlagAbility ?? [],
  );

  const {
    variables,
    isLoading: areVariablesLoading,
    isError: variablesError,
  } = useAccountVariables(accountId, {
    enabled: canUseTrainingVariables && !!accountId,
  });
  const { saveVariable } = useSaveAccountVariable(accountId ?? '');
  const existingItContact = variables?.find(
    (variable) => variable.type === AccountVariableType.IT_CONTACT,
  )?.value as ItContactValue | undefined;

  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);

  const awarenessSectionRef = useRef<HTMLDivElement>(null);
  const phishingSectionRef = useRef<HTMLDivElement>(null);
  const securityCultureSectionRef = useRef<HTMLDivElement>(null);
  const trainingVariablesSectionRef = useRef<HTMLDivElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isWhitelistingModalOpen, setIsWhitelistingModalOpen] = useState(false);

  const [courseMinimumScoreCheck, setCourseMinimumScoreCheck] = useState(false);
  const [courseMinimumScore, setCourseMinimumScore] = useState<number | null>(
    null,
  );
  const [courseReTrainGracePeriodDays, setCourseReTrainGracePeriodDays] =
    useState<number | null>(null);
  const [courseDueDaysCheck, setCourseDueDaysCheck] = useState(false);
  const [courseDueDays, setCourseDueDays] = useState<number | undefined>(14);
  const [notifyManagerForOverdueCourses, setNotifyManagerForOverdueCourses] =
    useState(false);
  const [managerReport, setManagerReport] = useState(false);
  const [managerReportFrequency, setManagerReportFrequency] = useState<
    AccountManagerReportFrequency | undefined
  >(undefined);
  const [enableAiTraining, setEnableAiTraining] = useState(false);

  const [scAutoScheduleEnabled, setScAutoScheduleEnabled] = useState(false);
  const [itContact, setItContact] = useState<ItContactValue>({
    name: '',
    contact: '',
    other: '',
  });
  const [itContactNameError, setItContactNameError] = useState<
    string | undefined
  >(undefined);

  const [phishingFollowUpEnabled, setPhishingFollowUpEnabled] = useState(false);
  const [phishingFollowUpEmailEnabled, setPhishingFollowUpEmailEnabled] =
    useState(false);

  const savedValuesRef = useRef({
    courseMinimumScore: null as number | null,
    courseReTrainGracePeriodDays: null as number | null,
    courseDueDays: null as number | null,
    notifyManagerForOverdueCourses: false,
    managerReport: false,
    managerReportFrequency: undefined as
      | AccountManagerReportFrequency
      | undefined,
    enableAiTraining: false,
    scAutoScheduleEnabled: false,
  });

  const isDirty = useMemo(() => {
    const saved = savedValuesRef.current;
    return (
      (courseMinimumScoreCheck ? courseMinimumScore : null) !==
        saved.courseMinimumScore ||
      courseReTrainGracePeriodDays !== saved.courseReTrainGracePeriodDays ||
      (courseDueDaysCheck ? courseDueDays ?? null : null) !==
        saved.courseDueDays ||
      (courseDueDaysCheck ? notifyManagerForOverdueCourses : false) !==
        saved.notifyManagerForOverdueCourses ||
      (courseDueDaysCheck ? managerReport : false) !== saved.managerReport ||
      (courseDueDaysCheck && managerReport
        ? managerReportFrequency
        : undefined) !== saved.managerReportFrequency ||
      enableAiTraining !== saved.enableAiTraining ||
      scAutoScheduleEnabled !== saved.scAutoScheduleEnabled
    );
  }, [
    courseMinimumScoreCheck,
    courseMinimumScore,
    courseReTrainGracePeriodDays,
    courseDueDaysCheck,
    courseDueDays,
    notifyManagerForOverdueCourses,
    managerReport,
    managerReportFrequency,
    enableAiTraining,
    scAutoScheduleEnabled,
  ]);

  const menuItems = useMemo(
    () => [
      { label: t('account.trainingSettings.awareness') },
      { label: t('account.trainingSettings.phishing') },
      { label: t('account.trainingSettings.securityCulture') },
      ...(canUseTrainingVariables
        ? [{ label: t('account.trainingSettings.variables') }]
        : []),
    ],
    [t, canUseTrainingVariables],
  );

  const scrollToSection = (index: number) => {
    setActiveIndex(index);
    const sections = [
      awarenessSectionRef,
      phishingSectionRef,
      securityCultureSectionRef,
      ...(canUseTrainingVariables ? [trainingVariablesSectionRef] : []),
    ];
    const section = sections[index]?.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const items = useMemo(
    () =>
      menuItems.map((item, index) => ({
        ...item,
        className: activeIndex === index ? 'active' : '',
        command: () => scrollToSection(index),
      })),
    [activeIndex, menuItems],
  );

  useEffect(() => {
    if (!account) return;

    savedValuesRef.current = {
      courseMinimumScore: account.courseMinimumScore ?? null,
      courseReTrainGracePeriodDays:
        account.courseReTrainGracePeriodDays ?? null,
      courseDueDays: account.courseDueDays ?? null,
      notifyManagerForOverdueCourses: account.courseDueDays
        ? !!account.meta?.notifyManagerForOverdueCourses
        : false,
      managerReport: account.courseDueDays ? !!account.managerReport : false,
      managerReportFrequency:
        account.courseDueDays && account.managerReport
          ? account.managerReportFrequency
          : undefined,
      enableAiTraining: !!account.enableAiTrainingForVulnerableUsers,
      scAutoScheduleEnabled: !!account.scAutoScheduleEnabled,
    };

    setCourseMinimumScoreCheck(!!account.courseMinimumScore);
    setCourseMinimumScore(account.courseMinimumScore ?? null);
    setCourseReTrainGracePeriodDays(
      account.courseReTrainGracePeriodDays ?? null,
    );
    setCourseDueDaysCheck(!!account.courseDueDays);
    setCourseDueDays(account.courseDueDays);
    setNotifyManagerForOverdueCourses(
      !!account.meta?.notifyManagerForOverdueCourses,
    );
    setManagerReport(!!account.managerReport);
    setManagerReportFrequency(account.managerReportFrequency);
    setEnableAiTraining(!!account.enableAiTrainingForVulnerableUsers);

    setScAutoScheduleEnabled(!!account.scAutoScheduleEnabled);

    setPhishingFollowUpEnabled(!!account.settings?.phishingFollowUpEnabled);
    setPhishingFollowUpEmailEnabled(
      !!account.settings?.phishingFollowUpEmailEnabled,
    );
  }, [account]);

  useEffect(() => {
    const existing = variables?.find(
      (variable) => variable.type === AccountVariableType.IT_CONTACT,
    )?.value as ItContactValue | undefined;

    setItContact({
      name: existing?.name ?? '',
      contact: existing?.contact ?? '',
      other: existing?.other ?? '',
    });
  }, [variables]);

  useEffect(() => {
    if (location.hash === '#phishing') {
      setActiveIndex(1);
      setTimeout(() => {
        phishingSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [location.hash]);

  const isAiTrainingToggleDisabled =
    !account?.isPhishingSenderWhitelisted || isBranchAdmin;
  const isScPaying = isScPayingCustomer(account);
  const isScToggleDisabled = !isScPaying || isBranchAdmin;

  const handleSave = async () => {
    if (!account?.id) return false;

    const trimmedItContactName = itContact.name.trim();
    const itContactTouched =
      canUseTrainingVariables &&
      (!!trimmedItContactName ||
        !!itContact.contact?.trim() ||
        !!itContact.other?.trim() ||
        !!existingItContact);

    if (itContactTouched && !trimmedItContactName) {
      setItContactNameError(t('error.requiredField'));

      return false;
    }
    setItContactNameError(undefined);

    setIsSubmitting(true);

    try {
      const response = await client.accounts.updateAccount({
        accountId: account.id,
        updates: {
          courseMinimumScore: courseMinimumScoreCheck
            ? courseMinimumScore
            : null,
          courseReTrainGracePeriodDays,
          courseDueDays: courseDueDaysCheck ? courseDueDays : null,
          notifyManagerForOverdueCourses: courseDueDaysCheck
            ? notifyManagerForOverdueCourses
            : false,
          managerReport: courseDueDaysCheck ? managerReport : false,
          managerReportFrequency:
            courseDueDaysCheck && managerReport
              ? managerReportFrequency
              : undefined,
          enableAiTrainingForVulnerableUsers: enableAiTraining,

          scAutoScheduleEnabled: isScPaying ? scAutoScheduleEnabled : undefined,

          settings: {
            phishingFollowUpEnabled,
            phishingFollowUpEmailEnabled: phishingFollowUpEnabled
              ? phishingFollowUpEmailEnabled
              : false,
          },
        },
      });

      if (itContactTouched) {
        await saveVariable({
          type: AccountVariableType.IT_CONTACT,
          payload: {
            name: trimmedItContactName,
            contact: itContact.contact || null,
            other: itContact.other || null,
          },
        });
      }

      if (response?.id === currentAccount?.id) {
        const accountData = await client.accounts.getAccount(response.id);
        dispatch(setCurrentAccount(accountData));
      }

      await refetchAccount();
      toast?.success(t('toast.success'), t('account.updated'));
      return true;
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isAccountLoading || !account ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {currentAccount && (
            <AppBreadCrumb
              model={getPathItems(account, currentAccount, can, t)}
            />
          )}

          <AccountHeader
            selectedAccount={account}
            title={t('account.trainingSettings')}
            isDirty={isDirty}
            onSave={handleSave}
            isSaving={isSubmitting}
          />

          <FlexContainer
            gap="var(--big-padding)"
            justify="flex-start"
            align="flex-start"
            style={{ marginTop: 'var(--large-padding)' }}
          >
            <InlineMenu items={items} />
            <div style={{ flex: 1 }}>
              <StyledSection ref={awarenessSectionRef} id="awareness">
                <StyledSectionHeading>
                  {t('account.trainingSettings.awareness')}
                </StyledSectionHeading>
                <StyledSectionDescription>
                  {t('account.trainingSettings.awareness.description')}
                </StyledSectionDescription>

                <StyledSubsectionHeading>
                  {t('account.trainingSettings')}
                </StyledSubsectionHeading>

                <div
                  className="field-checkbox mb-3"
                  data-id="courseMinimumScoreCheck"
                >
                  <Checkbox
                    inputId="courseMinimumScoreCheck"
                    checked={courseMinimumScoreCheck}
                    disabled={isBranchAdmin}
                    onChange={(e) => {
                      if (e.checked) {
                        setCourseMinimumScoreCheck(true);
                      } else {
                        setCourseMinimumScoreCheck(false);
                        setCourseMinimumScore(null);
                      }
                    }}
                  />
                  <label htmlFor="courseMinimumScoreCheck">
                    {t('account.trainingSettings.requiredScore')}
                  </label>
                  <AppIconTooltip
                    target="courseMinimumScoreCheck-tooltip"
                    content={t('account.set.minimumScoreForCompletion.tooltip')}
                  />
                </div>
                {courseMinimumScoreCheck && (
                  <div className="field w-full mb-3">
                    <InputNumber
                      id="courseMinimumScore"
                      value={courseMinimumScore}
                      className="w-full"
                      min={0}
                      max={100}
                      allowEmpty={false}
                      onValueChange={(e) => {
                        setCourseMinimumScore(e.value ?? null);
                      }}
                      showButtons
                      step={1}
                      suffix=" %"
                    />
                  </div>
                )}

                <div className="field w-full mb-3">
                  <label htmlFor="courseReTrainGracePeriodDays">
                    {t('account.trainingSettings.minimumDaysBeforeRetraining')}
                  </label>
                  <InputNumber
                    id="courseReTrainGracePeriodDays"
                    value={courseReTrainGracePeriodDays}
                    className="w-full"
                    min={0}
                    allowEmpty={false}
                    onValueChange={(e) => {
                      setCourseReTrainGracePeriodDays(e.value ?? null);
                    }}
                    showButtons
                    step={1}
                    suffix={` ${
                      courseReTrainGracePeriodDays === 1
                        ? t('generic.units.day')
                        : t('generic.units.days')
                    }`}
                  />
                </div>

                <div
                  className="field-checkbox mb-3"
                  data-id="courseDueDaysCheck"
                >
                  <Checkbox
                    inputId="courseDueDaysCheck"
                    checked={courseDueDaysCheck}
                    disabled={isBranchAdmin}
                    onChange={(e) => {
                      if (e.checked) {
                        setCourseDueDaysCheck(true);
                        setCourseDueDays(14);
                      } else {
                        setCourseDueDaysCheck(false);
                        setCourseDueDays(undefined);
                      }
                    }}
                  />
                  <label htmlFor="courseDueDaysCheck">
                    {t('account.trainingSettings.enableDueDates')}
                  </label>
                  <AppIconTooltip
                    target="courseDueDaysCheck-tooltip"
                    content={t('account.set.contentDueDays.tooltip')}
                  />
                </div>

                {courseDueDaysCheck && (
                  <div className="field w-full">
                    <label htmlFor="courseDueDays">
                      {t('account.trainingSettings.daysToComplete')}
                    </label>
                    <InputNumber
                      id="courseDueDays"
                      value={courseDueDays ?? null}
                      className="w-full"
                      min={1}
                      step={1}
                      allowEmpty
                      onValueChange={(e) => {
                        setCourseDueDays(e.value ?? undefined);
                      }}
                      showButtons
                      disabled={isBranchAdmin}
                      suffix={` ${
                        courseDueDays === 1
                          ? t('generic.units.day')
                          : t('generic.units.days')
                      }`}
                    />
                  </div>
                )}

                <StyledSubsectionHeading>
                  {t('account.trainingSettings.managerFollowUp')}
                </StyledSubsectionHeading>

                <div
                  className="field-checkbox mb-3"
                  data-id="notifyManagerForOverdueCourses"
                >
                  <Checkbox
                    inputId="notifyManagerForOverdueCourses"
                    checked={notifyManagerForOverdueCourses}
                    disabled={isBranchAdmin}
                    onChange={(e) => {
                      setNotifyManagerForOverdueCourses(!!e.checked);
                    }}
                  />
                  <label htmlFor="notifyManagerForOverdueCourses">
                    {t('account.trainingSettings.ccManagerOnOverdue')}
                  </label>
                  <AppIconTooltip
                    target="notifyManagerForOverdueCourses-tooltip"
                    content={t(
                      'account.enable.notifyManagerForOverdueContent.tooltip',
                    )}
                  />
                </div>

                <div className="field-checkbox mb-3" data-id="managerReport">
                  <Checkbox
                    inputId="managerReport"
                    checked={managerReport}
                    disabled={isBranchAdmin}
                    onChange={(e) => {
                      setManagerReport(!!e.checked);
                    }}
                  />
                  <label htmlFor="managerReport">
                    {t('account.trainingSettings.sendManagerReport')}
                  </label>
                  <AppIconTooltip
                    target="managerReport-tooltip"
                    content={t('account.enable.managerReport.tooltip')}
                  />
                </div>
                {managerReport && (
                  <div className="field w-full mb-3">
                    <label htmlFor="managerReportFrequency">
                      {t('account.managerReport.frequency')}
                    </label>
                    <Dropdown
                      id="managerReportFrequency"
                      value={managerReportFrequency}
                      onChange={(e: DropdownChangeEvent) => {
                        setManagerReportFrequency(e.value);
                      }}
                      className="w-full"
                      placeholder={t('generic.select')}
                      options={[
                        {
                          label: t('account.managerReport.frequency.weekly'),
                          value: AccountManagerReportFrequency.WEEKLY,
                        },
                        {
                          label: t('account.managerReport.frequency.monthly'),
                          value: AccountManagerReportFrequency.MONTHLY,
                        },
                      ]}
                      disabled={isBranchAdmin}
                      {...preventDropdownBrowserTranslate}
                    />
                  </div>
                )}
              </StyledSection>

              <StyledSection ref={phishingSectionRef} id="phishing">
                <StyledSectionHeading>
                  {t('account.trainingSettings.phishing.heading')}
                </StyledSectionHeading>
                <StyledSectionDescription>
                  {t('account.trainingSettings.phishing.description')}
                </StyledSectionDescription>

                <div
                  className="field-checkbox mb-3"
                  style={{ marginTop: '28px' }}
                  id="ai-training-toggle"
                >
                  <Checkbox
                    inputId="enableAiTraining"
                    checked={enableAiTraining}
                    onChange={(e) => {
                      setEnableAiTraining(!!e.checked);
                    }}
                    disabled={isAiTrainingToggleDisabled}
                  />
                  <StyledCheckboxLabel htmlFor="enableAiTraining">
                    {t('account.trainingSettings.phishing.aiTraining.label')}
                    <AppIconTooltip
                      target="ai-training-info"
                      content={t(
                        'account.trainingSettings.phishing.aiTraining.tooltip',
                      )}
                    />
                  </StyledCheckboxLabel>
                </div>
                <StyledCheckboxDescription>
                  {t(
                    'account.trainingSettings.phishing.aiTraining.description',
                  )}
                </StyledCheckboxDescription>
                {isAiTrainingToggleDisabled &&
                  !account?.isPhishingSenderWhitelisted && (
                    <Tooltip
                      target="#ai-training-toggle"
                      content={t(
                        'account.trainingSettings.phishing.aiTraining.whitelistingRequired',
                      )}
                      position="top"
                    />
                  )}

                {accountCanUseFeature(
                  PostHogFeatureFlags.PHISHING_SIMULATION_FOLLOW_UP,
                  account?.featureFlagAbility ?? [],
                ) && (
                  <>
                    <div
                      className="field-checkbox mb-3"
                      style={{ marginTop: '28px' }}
                      data-id="phishingFollowUpEnabled"
                    >
                      <Checkbox
                        inputId="phishingFollowUpEnabled"
                        checked={phishingFollowUpEnabled}
                        disabled={isBranchAdmin}
                        onChange={(e) => {
                          const checked = !!e.checked;
                          setPhishingFollowUpEnabled(checked);
                          if (!checked) {
                            setPhishingFollowUpEmailEnabled(false);
                          }
                        }}
                      />
                      <StyledCheckboxLabel htmlFor="phishingFollowUpEnabled">
                        {t(
                          'account.trainingSettings.phishing.endUserResults.label',
                        )}
                      </StyledCheckboxLabel>
                    </div>
                    <StyledCheckboxDescription>
                      {t(
                        'account.trainingSettings.phishing.endUserResults.description',
                      )}
                    </StyledCheckboxDescription>

                    {phishingFollowUpEnabled && (
                      <div style={{ marginLeft: '33px', marginTop: '12px' }}>
                        <div
                          className="field-checkbox mb-3"
                          data-id="phishingFollowUpEmailEnabled"
                        >
                          <Checkbox
                            inputId="phishingFollowUpEmailEnabled"
                            checked={phishingFollowUpEmailEnabled}
                            disabled={isBranchAdmin}
                            onChange={(e) =>
                              setPhishingFollowUpEmailEnabled(!!e.checked)
                            }
                          />
                          <StyledCheckboxLabel htmlFor="phishingFollowUpEmailEnabled">
                            {t(
                              'account.trainingSettings.phishing.emailNotifications.label',
                            )}
                          </StyledCheckboxLabel>
                        </div>
                        <StyledCheckboxDescription>
                          {t(
                            'account.trainingSettings.phishing.emailNotifications.description',
                          )}
                        </StyledCheckboxDescription>
                        <StyledFollowUpToast>
                          <i className="pi pi-exclamation-triangle" />
                          <span>
                            <strong>
                              {t(
                                'account.trainingSettings.phishing.emailNotifications.toast.note',
                              )}
                            </strong>{' '}
                            {t(
                              'account.trainingSettings.phishing.emailNotifications.toast.body',
                            )}
                          </span>
                        </StyledFollowUpToast>
                      </div>
                    )}
                  </>
                )}

                <WhitelistingStatusBanner
                  isCompleted={!!account?.isPhishingSenderWhitelisted}
                  onStartWhitelisting={() => setIsWhitelistingModalOpen(true)}
                  className="mt-4"
                />
              </StyledSection>

              <StyledSection
                ref={securityCultureSectionRef}
                id="security-culture"
              >
                <StyledSectionHeading>
                  {t('account.trainingSettings.securityCulture')}
                </StyledSectionHeading>
                <StyledSectionDescription>
                  {t('account.trainingSettings.securityCulture.description')}
                </StyledSectionDescription>

                <StyledSubsectionHeading>
                  {t('security.culture.planner.auto.schedule')}
                </StyledSubsectionHeading>

                <div className="field-checkbox mb-3">
                  <span id="sc-auto-schedule-checkbox">
                    <Checkbox
                      inputId="scAutoScheduleEnabled"
                      checked={scAutoScheduleEnabled}
                      onChange={(e) => setScAutoScheduleEnabled(!!e.checked)}
                      disabled={isScToggleDisabled}
                    />
                  </span>
                  <label htmlFor="scAutoScheduleEnabled">
                    {t(
                      'account.trainingSettings.securityCulture.autoSchedule.label',
                    )}
                  </label>
                  <AppIconTooltip
                    target="sc-auto-schedule-info"
                    content={t(
                      'security.culture.send.survey.auto.schedule.tooltip.paying',
                    )}
                    position="right"
                  />
                </div>
                {!isScPaying && (
                  <Tooltip
                    target="#sc-auto-schedule-checkbox"
                    content={t(
                      'account.trainingSettings.securityCulture.autoSchedule.disabledTooltip',
                    )}
                    position="top"
                  />
                )}
              </StyledSection>

              {canUseTrainingVariables && (
                <StyledSection
                  ref={trainingVariablesSectionRef}
                  id="training-variables"
                >
                  <StyledSectionHeading>
                    {t('account.trainingSettings.variables')}
                  </StyledSectionHeading>
                  <StyledSectionDescription>
                    {t('account.trainingSettings.variables.description')}
                  </StyledSectionDescription>

                  <div className="mt-4 w-full">
                    <TrainingVariablesSection
                      value={itContact}
                      onChange={(next) => {
                        setItContact(next);
                        if (itContactNameError) {
                          setItContactNameError(undefined);
                        }
                      }}
                      nameError={itContactNameError}
                      isFilled={!!existingItContact?.name}
                      isLoading={areVariablesLoading}
                      isError={variablesError}
                    />
                  </div>
                </StyledSection>
              )}
            </div>
          </FlexContainer>

          {accountId && (
            <WhitelistingModal
              isOpen={isWhitelistingModalOpen}
              onClose={() => {
                setIsWhitelistingModalOpen(false);
                refetchAccount();
              }}
              accountId={accountId}
            />
          )}
        </>
      )}
    </>
  );
};
